/**
 * TemplatePreview Component
 *
 * Renders a read-only mini kanban board from a board template,
 * populated with sample cards. Reusable in both TemplatesTable
 * and CreateBoardFromTemplateForm.
 */

import { useState } from 'react';
import { useThemeColors, withOpacity, opacity } from '@/styles';
import { BoardTaskPriority } from '@/types/enums/BoardTaskPriority';
import type { BoardTask } from '@/types/models/BoardTask';
import type {
  GetBoardTemplatesTemplatesItem,
  GetBoardTemplatesStatusesItem,
} from '@/types/generated/processor-types';
import type { ColumnConfig } from '@/types/board-config.types';

interface TemplatePreviewProps {
  template: GetBoardTemplatesTemplatesItem;
  /** Render at a smaller scale for inline use (e.g. inside a dialog) */
  compact?: boolean;
}

const SAMPLE_CARDS: {
  title: string;
  priority: BoardTaskPriority;
  description?: string;
  estimated_pomodoros?: number;
}[] = [
  {
    title: 'Example Task',
    priority: BoardTaskPriority.HIGH,
    estimated_pomodoros: 3,
  },
  {
    title: 'Getting Started',
    priority: BoardTaskPriority.MEDIUM,
    description: 'A sample card',
    estimated_pomodoros: 2,
  },
  {
    title: 'In Review',
    priority: BoardTaskPriority.LOW,
    estimated_pomodoros: 1,
  },
];

function buildSampleCards(statusId: string, statusIndex: number): BoardTask[] {
  // Give each column a different slice of sample cards (1-3 cards)
  const count = statusIndex === 0 ? 3 : statusIndex === 1 ? 2 : 1;
  return SAMPLE_CARDS.slice(0, count).map((sample, i) => ({
    id: `preview-${statusId}-${i}`,
    board_id: 'preview',
    organization_id: 'preview',
    status_id: statusId,
    title: sample.title,
    description: sample.description,
    priority: sample.priority,
    estimated_pomodoros: sample.estimated_pomodoros,
    created_by: 'preview',
    created_at: '1970-01-01T00:00:00.000Z',
  }));
}

function PreviewCard({
  card,
  colors,
}: {
  card: BoardTask;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const getPriorityStyle = (priority: string) => {
    switch (priority) {
      case BoardTaskPriority.HIGH:
        return {
          backgroundColor: withOpacity(colors.error, opacity.medium),
          color: colors.error,
        };
      case BoardTaskPriority.MEDIUM:
        return {
          backgroundColor: withOpacity(colors.primary, opacity.medium),
          color: colors.primary,
        };
      case BoardTaskPriority.LOW:
        return {
          backgroundColor: withOpacity(colors.success, opacity.medium),
          color: colors.success,
        };
      default:
        return {
          backgroundColor: withOpacity(colors.primary, opacity.medium),
          color: colors.primary,
        };
    }
  };

  return (
    <div
      className="p-3 rounded-lg flex flex-col gap-1.5"
      style={{
        backgroundColor: colors.background,
        minHeight: '80px',
      }}
    >
      <span
        className="font-medium text-xs leading-tight"
        style={{ color: colors.text.primary }}
      >
        {card.title}
      </span>
      {card.description && (
        <span
          className="text-xs leading-tight"
          style={{ color: colors.text.secondary }}
        >
          {card.description}
        </span>
      )}
      {(card.priority || card.estimated_pomodoros) && (
        <div className="flex gap-1.5 mt-auto flex-wrap">
          {card.priority && (
            <span
              className="px-2 py-0.5 rounded text-xs"
              style={getPriorityStyle(card.priority)}
            >
              {card.priority}
            </span>
          )}
          {card.estimated_pomodoros && (
            <span
              className="px-2 py-0.5 rounded text-xs"
              style={{
                backgroundColor: withOpacity(colors.blue, opacity.medium),
                color: colors.blue,
              }}
            >
              🍅 {card.estimated_pomodoros}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function ColumnRulesPopover({
  config,
  colors,
}: {
  config: ColumnConfig;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const [open, setOpen] = useState(false);

  const hasRules =
    config.maxCards != null ||
    config.forwardOnly ||
    config.autoAssign ||
    config.notifyOnEnter;

  if (!hasRules) return null;

  return (
    <div className="relative flex-shrink-0">
      <button
        className="flex items-center justify-center w-5 h-5 rounded-full text-xs transition-colors"
        style={{
          backgroundColor: withOpacity(colors.primary, opacity.light),
          color: colors.primary,
        }}
        title="Column rules"
        onClick={e => {
          e.stopPropagation();
          setOpen(prev => !prev);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      >
        ⓘ
      </button>
      {open && (
        <div
          className="absolute top-6 left-0 z-20 rounded-lg p-2.5 shadow-lg text-xs space-y-1 whitespace-nowrap"
          style={{
            backgroundColor: colors.background,
            border: `1px solid ${withOpacity(colors.text.secondary, opacity.light)}`,
            minWidth: '140px',
          }}
        >
          <div
            className="font-semibold mb-1"
            style={{ color: colors.text.primary }}
          >
            Column Rules
          </div>
          {config.maxCards != null && (
            <div style={{ color: colors.text.secondary }}>
              Max cards/week:{' '}
              <strong style={{ color: colors.text.primary }}>
                {config.maxCards}
              </strong>
            </div>
          )}
          {config.forwardOnly && (
            <div style={{ color: colors.text.secondary }}>Forward only</div>
          )}
          {config.autoAssign && (
            <div style={{ color: colors.text.secondary }}>Auto-assign</div>
          )}
          {config.notifyOnEnter && (
            <div style={{ color: colors.text.secondary }}>Notify on enter</div>
          )}
        </div>
      )}
    </div>
  );
}

function PreviewColumn({
  status,
  statusIndex,
  colors,
}: {
  status: GetBoardTemplatesStatusesItem;
  statusIndex: number;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const cards = buildSampleCards(status.id, statusIndex);
  const columnConfig = (status.config || {}) as ColumnConfig;

  return (
    <div className="flex-shrink-0" style={{ width: '220px' }}>
      <div
        className="rounded-xl p-3 h-full flex flex-col shadow-soft"
        style={{ backgroundColor: colors.offWhite }}
      >
        {/* Column Header */}
        <div className="flex items-center gap-2 mb-3 flex-shrink-0">
          {status.color && (
            <div
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: status.color }}
            />
          )}
          <h4
            className="font-semibold text-xs truncate"
            style={{ color: colors.text.primary }}
            title={status.name}
          >
            {status.name}
          </h4>
          <span
            className="text-xs flex-shrink-0"
            style={{ color: colors.text.secondary }}
          >
            ({cards.length})
          </span>
          <ColumnRulesPopover config={columnConfig} colors={colors} />
        </div>

        {/* Cards */}
        <div className="space-y-2 flex-1 overflow-y-auto">
          {cards.map(card => (
            <PreviewCard key={card.id} card={card} colors={colors} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function TemplatePreview({
  template,
  compact = false,
}: TemplatePreviewProps) {
  const colors = useThemeColors();
  const sortedStatuses = [...template.statuses].sort(
    (a, b) => a.position - b.position
  );

  return (
    <div className={compact ? 'py-2' : 'py-4'}>
      {/* Scrollable columns */}
      <div
        className="flex gap-3 overflow-x-auto pb-2"
        style={{ minHeight: compact ? '220px' : '300px' }}
      >
        {sortedStatuses.map((status, index) => (
          <PreviewColumn
            key={status.id}
            status={status}
            statusIndex={index}
            colors={colors}
          />
        ))}
      </div>
    </div>
  );
}
